import { z } from 'zod'
import { idSchema } from './common'
import { inventoryFamilySchema, customFieldInputSchema } from './inventory'

const optionalText = z.string().trim().min(1).max(2000).optional()

export const inventoryYamlManufacturerSchema = z
  .object({
    id: idSchema,
    name: z.string().trim().min(1).max(160),
    description: optionalText,
    website: z.url().max(2048).optional(),
  })
  .strict()

export const inventoryYamlCategorySchema = z
  .object({
    id: idSchema,
    family: inventoryFamilySchema,
    name: z.string().trim().min(1).max(160),
    description: optionalText,
    invoicePolicy: z.enum(['disabled', 'optional', 'required']).optional(),
  })
  .strict()

export const inventoryYamlModelSchema = z
  .object({
    id: idSchema,
    family: inventoryFamilySchema,
    categoryId: idSchema,
    manufacturerId: idSchema.optional(),
    name: z.string().trim().min(1).max(160),
    modelNumber: z.string().trim().min(1).max(120).optional(),
    notes: optionalText,
  })
  .strict()

export const inventoryYamlConsumableSchema = z
  .object({
    id: idSchema,
    modelId: idSchema,
    name: z.string().trim().min(1).max(160),
    unit: z.string().trim().min(1).max(40),
    precision: z.number().int().min(0).max(6).default(0),
    notes: optionalText,
    minimumQuantity: z.number().nonnegative().max(1_000_000_000).optional(),
    targetQuantity: z.number().nonnegative().max(1_000_000_000).optional(),
  })
  .strict()
  .refine(
    (value) =>
      value.minimumQuantity === undefined ||
      value.targetQuantity === undefined ||
      value.targetQuantity >= value.minimumQuantity,
    {
      message: 'targetQuantity doit être supérieur ou égal à minimumQuantity',
      path: ['targetQuantity'],
    },
  )

export const inventoryYamlSchema = z
  .object({
    manufacturers: z.array(inventoryYamlManufacturerSchema).max(500).default([]),
    categories: z.array(inventoryYamlCategorySchema).max(500).default([]),
    models: z.array(inventoryYamlModelSchema).max(2000).default([]),
    consumables: z.array(inventoryYamlConsumableSchema).max(2000).default([]),
    customFields: z
      .array(
        z
          .object({
            field: customFieldInputSchema,
            archived: z.boolean().default(false),
            bindings: z
              .array(
                z
                  .object({
                    targetType: z.enum(['category', 'model', 'resource_type']),
                    targetId: z.string().trim().min(1).max(160),
                  })
                  .strict(),
              )
              .max(500),
          })
          .strict(),
      )
      .max(500)
      .default([]),
  })
  .strict()
  .superRefine((inventory, context) => {
    const unique = (items: Array<{ id: string }>, path: string) => {
      const seen = new Set<string>()
      items.forEach((item, index) => {
        if (seen.has(item.id))
          context.addIssue({
            code: 'custom',
            path: [path, index, 'id'],
            message: `identifiant dupliqué : ${item.id}`,
          })
        seen.add(item.id)
      })
      return seen
    }
    const manufacturers = unique(inventory.manufacturers, 'manufacturers')
    unique(inventory.categories, 'categories')
    const models = unique(inventory.models, 'models')
    unique(inventory.consumables, 'consumables')
    const fieldKeys = new Set<string>()
    inventory.customFields.forEach((item, index) => {
      if (fieldKeys.has(item.field.key))
        context.addIssue({
          code: 'custom',
          path: ['customFields', index],
          message: 'Clé de champ dupliquée',
        })
      fieldKeys.add(item.field.key)
      item.bindings.forEach((binding) => {
        // Resource types are resolved by their stable key within the destination tenant.
        if (binding.targetType === 'resource_type') return
        const candidates = binding.targetType === 'model' ? inventory.models : inventory.categories
        if (!candidates.some((candidate) => candidate.id === binding.targetId))
          context.addIssue({
            code: 'custom',
            path: ['customFields', index, 'bindings'],
            message: 'Affectation inconnue',
          })
      })
    })

    inventory.models.forEach((model, index) => {
      const category = inventory.categories.find((item) => item.id === model.categoryId)
      if (!category)
        context.addIssue({
          code: 'custom',
          path: ['models', index, 'categoryId'],
          message: `catégorie inconnue : ${model.categoryId}`,
        })
      else if (category.family !== model.family)
        context.addIssue({
          code: 'custom',
          path: ['models', index, 'family'],
          message: 'la famille doit correspondre à celle de la catégorie',
        })
      if (model.manufacturerId && !manufacturers.has(model.manufacturerId))
        context.addIssue({
          code: 'custom',
          path: ['models', index, 'manufacturerId'],
          message: `fabricant inconnu : ${model.manufacturerId}`,
        })
    })
    inventory.consumables.forEach((consumable, index) => {
      const model = inventory.models.find((item) => item.id === consumable.modelId)
      if (!models.has(consumable.modelId))
        context.addIssue({
          code: 'custom',
          path: ['consumables', index, 'modelId'],
          message: `modèle inconnu : ${consumable.modelId}`,
        })
      else if (model?.family !== 'consumable')
        context.addIssue({
          code: 'custom',
          path: ['consumables', index, 'modelId'],
          message: 'un consommable doit référencer un modèle de famille consumable',
        })
    })
  })

export type InventoryYaml = z.infer<typeof inventoryYamlSchema>
